import keyControls from "./key-controls";
import { IDS, YT_VIDEO_STATE } from "../constants";
import { DEFAULT_YT_VIDEO_ID } from "../config";

import { defaultThumbnail } from "../theme/uploaded";
const LOG = (...arg) => console.log("%%% PM %%% ", ...arg);

const INIT_PLAYER_STATE = {
  isPlaying: false,
  playbackCovered: 0,
  playbackLength: 0,
  currentSongIndex: 0,
  time: "",
  queue: [],
  thumbnail: defaultThumbnail,
  title: "Play a song",
};

const PlayerManager = class {
  constructor() {
    this.uiStateSetter = null;
    this.uiQueueSetter = null;
    this.player = null;
    this.autoplay = false;
    this.state = INIT_PLAYER_STATE;
    this.queue = [];
    //add key controls
    keyControls();
  }

  onSetState = () => {
    if (this.uiStateSetter) this.uiStateSetter(this.state);
  };
  onSetQueue = () => {
    if (this.uiQueueSetter) this.uiQueueSetter(this.queue);
  };
  debounce = (func, delay) => {
    let debounceTimer;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  };
  newStateCaller = this.debounce(this.onSetState, 300);
  newQueueCaller = this.debounce(this.onSetQueue, 300);

  setState(newState) {
    // merge changes with current state
    this.state = { ...this.state, ...newState };
    this.newStateCaller();
  }

  setQueue(queue) {
    this.queue = queue;
    this.newQueueCaller();
  }

  getState() {
    return this.state;
  }

  registerPlayer = (p) => {
    this.player = p;
    this.setState({});
    this.setVolume(100);
  };

  registerStateManager = ([setstate, setPlayerQueue]) => {
    this.uiStateSetter = setstate;
    this.uiQueueSetter = setPlayerQueue;
  };

  onVideoStateChange = (event) => {
    const iframState = event.data;
    let totalTime = "";
    switch (iframState) {
      case YT_VIDEO_STATE.UNSTARTED:
        return;
      case YT_VIDEO_STATE.ENDED:
        this.playNext();
        return;
      case YT_VIDEO_STATE.PLAYING:
        totalTime = this.player.getDuration();
        this.setState({
          isPlaying: true,
          playbackCovered: this.player.getCurrentTime(),
          playbackLength:
            totalTime !== 0 ? totalTime : this.state.playbackLength,
          time: new Date().getTime(),
        });
        return;
      case YT_VIDEO_STATE.PAUSED:
        totalTime = this.player.getDuration();
        this.setState({
          isPlaying: false,
          playbackCovered: this.player.getCurrentTime(),
          playbackLength:
            totalTime !== 0 ? totalTime : this.state.playbackLength,
          time: new Date().getTime(),
        });
        return;
      case YT_VIDEO_STATE.BUFFERING:
        return;
      case YT_VIDEO_STATE.VIDEO_CUED:
        LOG("autoplay", this.autoplay);
        if (this.autoplay) this.player.playVideo();
        return;
    }
  };

  reset = (newQueue) => {
    const queue =
      newQueue && newQueue.length > 0
        ? newQueue
        : [
            {
              title: "Empty Queue",
              defaultThumbnail: defaultThumbnail,
              videoId: DEFAULT_YT_VIDEO_ID,
            },
          ];
    // const queue = newQueue || this.queue;
    if (newQueue) this.setQueue(queue);

    this.autoplay = false;

    let firstSong = "";
    if (queue.length > 0) firstSong = queue[0];
    if (firstSong && firstSong.videoId && this.player)
      this.player.cueVideoById(firstSong.videoId);

    this.setState({
      title: firstSong ? firstSong.title : "",
      isPlaying: false,
      currentSongIndex: 0,
      playbackCovered: 0,
      playbackLength: 0,
      thumbnail: firstSong
        ? firstSong.defaultThumbnail
          ? firstSong.defaultThumbnail
          : firstSong.thumbnails &&
            (firstSong.thumbnails.mediumUrl || firstSong.thumbnails.standardUrl)
        : "",
    });
  };

  playAll(queue, idx) {
    this.autoplay = true;
    let song = queue.length >= 0 && (idx ? queue[idx] : queue[0]);
    if (song && song.videoId && this.player)
      this.player.cueVideoById(song.videoId);
    this.setQueue(queue);
    this.setState({
      title: song ? song.title : "",
      isPlaying: false,
      currentSongIndex: idx || 0,
      thumbnail: song
        ? song.thumbnails.mediumUrl || song.thumbnails.standardUrl
        : "",
    });
  }

  playAtIdx = (idx) => {
    this.autoplay = true;
    let song = this.queue.length >= 0 && (idx >= 0 ? this.queue[idx] : "");
    if (song && song.videoId && this.player)
      this.player.cueVideoById(song.videoId);
    this.setState({
      title: song ? song.title : "",
      isPlaying: false,
      currentSongIndex: idx || 0,
      thumbnail: song
        ? song.thumbnails.mediumUrl || song.thumbnails.standardUrl
        : "",
    });
  };

  addToQueue = (songs) => {
    const queue = this.queue;
    const isEmptyQueue = this.queue.length === 0;
    songs.forEach((song) => {
      queue.push(song);
    });
    this.setQueue(queue);

    if (isEmptyQueue) {
      this.autoplay = false;
      const idx = 0;
      let song = queue[0];
      if (song && song.videoId && this.player)
        this.player.cueVideoById(song.videoId);
      this.setState({
        title: song ? song.title : "",
        isPlaying: false,
        currentSongIndex: idx || 0,
        thumbnail: song
          ? song.thumbnails.mediumUrl || song.thumbnails.standardUrl
          : "",
      });
    }
  };

  updateQueue = (newQueue) => {
    let queue = this.queue;
    const isEmptyQueue = this.queue.length === 0;
    queue = newQueue;
    this.setQueue(newQueue);
    if (isEmptyQueue) {
      this.autoplay = false;
      const idx = 0;
      let song = queue[0];
      if (song && song.videoId && this.player)
        this.player.cueVideoById(song.videoId);
      this.setState({
        title: song ? song.title : "",
        isPlaying: false,
        currentSongIndex: idx || 0,
        thumbnail: song
          ? song.thumbnails.mediumUrl || song.thumbnails.standardUrl
          : "",
      });
    }
  };

  deleteFromQueue = (ridx) => {
    const queue = this.queue;
    if (ridx < 0 || ridx >= queue.length) return;
    const { currentSongIndex: csi } = this.state;
    queue.splice(ridx, 1);
    this.setQueue(queue);
    if (ridx < csi)
      this.setState({
        currentSongIndex: ridx < csi ? csi - 1 : csi,
      });
  };

  playNext = () => {
    const state = this.state;
    const queue = this.queue;
    if (queue.length === 0) return;
    const { currentSongIndex } = state;
    const nextSongIndex = (currentSongIndex + 1) % queue.length;
    this.playAtIdx(nextSongIndex);
  };

  playPrev = () => {
    const state = this.state;
    const queue = this.queue;
    if (queue.length === 0) return;
    const { currentSongIndex } = state;
    const prevSongIndex = (currentSongIndex - 1 + queue.length) % queue.length;
    this.playAtIdx(prevSongIndex);
  };

  pause = () => {
    if (this.player) this.player.pauseVideo();
  };

  resume = () => {
    const queue = this.queue;
    if (queue.length === 0) return;
    this.autoplay = true;
    if (this.player) this.player.playVideo();
  };

  seek = (seekTo) => {
    if (this.player) this.player.seekTo(seekTo);
  };

  setVolume = (volume) => {
    if (volume && volume >= 0 && volume <= 100 && this.player)
      this.player.setVolume(volume);
    this.setState({ volume });
  };

  // update queue on songs added to room
};

const instance = new PlayerManager();

export default instance;
